// api/issues.js
// API client for fast-loading issues endpoints

const BASE = '/api/issues';

/**
 * Fetch paginated issues index
 * @param {string} scope - 'my' or 'others'
 * @param {string | null} cursor - Pagination cursor (null for first page)
 * @param {number} pageSize - Items per page (25-500)
 * @param {boolean} includeDone - Include Done items (default false)
 * @param {string} authToken - Authorization token
 * @param {AbortSignal} [signal] - Abort signal for cancellation
 * @returns {Promise<import('../types/issues').IndexResponse>}
 */
export async function fetchIndex(scope, cursor, pageSize = 100, includeDone = false, authToken, signal) {
  const q = new URLSearchParams({ scope, pageSize: String(pageSize) });
  if (cursor) q.set('cursor', cursor);
  if (includeDone) q.set('includeDone', 'true');

  const res = await fetch(`${BASE}/index?${q.toString()}`, {
    signal,
    headers: {
      'Authorization': authToken
    }
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: 'unknown' }));
    throw new Error(`fetchIndex ${res.status}: ${error.error || 'failed'}`);
  }

  return res.json();
}

/**
 * Fetch issues changed since a timestamp (delta sync)
 * @param {string} scope - 'my' or 'others'
 * @param {string} sinceIso - ISO timestamp to fetch changes since
 * @param {number} limit - Max items to return (1-1000)
 * @param {boolean} includeDone - Include Done items (default false)
 * @param {string} authToken - Authorization token
 * @param {AbortSignal} [signal] - Abort signal for cancellation
 * @returns {Promise<import('../types/issues').ChangesResponse>}
 */
export async function fetchChanges(scope, sinceIso, limit = 500, includeDone = false, authToken, signal) {
  const q = new URLSearchParams({ scope, since: sinceIso, limit: String(limit) });
  if (includeDone) q.set('includeDone', 'true');

  const res = await fetch(`${BASE}/changes?${q.toString()}`, {
    signal,
    headers: {
      'Authorization': authToken
    }
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: 'unknown' }));
    throw new Error(`fetchChanges ${res.status}: ${error.error || 'failed'}`);
  }

  return res.json();
}

/**
 * Fetch a single issue by key
 * @param {string} issueKey - Issue key (e.g., 'PROJ-123')
 * @param {string} authToken - Authorization token
 * @param {AbortSignal} [signal] - Abort signal for cancellation
 * @returns {Promise<{issue: import('../types/issues').NormalizedIssue}>}
 * @throws {Error} With error.code = 'NOT_FOUND' if issue doesn't exist or no access
 */
export async function fetchIssue(issueKey, authToken, signal) {
  const res = await fetch(`${BASE}/${issueKey}`, {
    signal,
    headers: {
      'Authorization': authToken
    }
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({ error: 'unknown' }));
    const error = new Error(errorData.message || `fetchIssue ${res.status}: ${errorData.error || 'failed'}`);
    error.code = errorData.error || 'UNKNOWN';
    error.status = res.status;
    throw error;
  }

  return res.json();
}
